export interface PointerDragDelta {
  dx: number;
  dy: number;
}

export interface PointerDragHandlers {
  threshold?: number;
  // Pointer capture keeps events targeting originEl even once the cursor leaves it — useful
  // for a marquee that fills the viewport, but it also retargets the eventual "click" to
  // originEl (see StringMarquee's onHostClick), which would swallow clicks on any real
  // interactive content dragged over (a button/link inside a carousel slide, say). Default
  // true to keep existing callers' behavior; pass false where that retargeting is unwanted.
  capture?: boolean;
  onMove(event: PointerEvent, delta: PointerDragDelta): void;
  onEnd?(event: PointerEvent): void;
  onCancel?(): void;
}

export function startPointerDrag(
  startEvent: PointerEvent,
  handlers: PointerDragHandlers,
): () => void {
  const originEl = startEvent.currentTarget as Element | null;
  const startX = startEvent.clientX;
  const startY = startEvent.clientY;
  const pointerId = startEvent.pointerId;
  const threshold = handlers.threshold ?? 0;
  const capture = handlers.capture ?? true;

  let started = threshold === 0;
  let disposed = false;

  try {
    if (capture && originEl && "setPointerCapture" in originEl) {
      (originEl as Element & { setPointerCapture(id: number): void }).setPointerCapture(pointerId);
    }
  } catch {
    /* capture may fail on synthetic events */
  }

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onEnd);
    document.removeEventListener("pointercancel", onCancel);
    document.removeEventListener("keydown", onKey);
    try {
      if (capture && originEl && "releasePointerCapture" in originEl) {
        (
          originEl as Element & { releasePointerCapture(id: number): void }
        ).releasePointerCapture(pointerId);
      }
    } catch {
      /* ignore */
    }
  };

  const onMove = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (!started) {
      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
      started = true;
    }
    handlers.onMove(e, { dx, dy });
  };

  const onEnd = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    dispose();
    if (started) {
      handlers.onEnd?.(e);
    }
  };

  const onCancel = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    dispose();
    handlers.onCancel?.();
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    dispose();
    handlers.onCancel?.();
  };

  document.addEventListener("pointermove", onMove);
  document.addEventListener("pointerup", onEnd);
  document.addEventListener("pointercancel", onCancel);
  document.addEventListener("keydown", onKey);

  return dispose;
}
